import { useState } from "react";
import { ChevronRight, Wrench, AlertCircle, CheckCircle2, Loader2 } from "lucide-react";
import type { ToolCall } from "../stores/chat";

export interface ToolCallCardProps {
  call: ToolCall;
}

function fmt(value: unknown): string {
  if (value === undefined) return "";
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

export function ToolCallCard({ call }: ToolCallCardProps) {
  const [open, setOpen] = useState(false);
  const state: "running" | "ok" | "err" = call.error
    ? "err"
    : call.result !== undefined
      ? "ok"
      : "running";

  return (
    <div className="card my-1 overflow-hidden text-[12px]">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left hover:bg-raised"
        aria-expanded={open}
      >
        <ChevronRight
          size={12}
          className={`text-tertiary transition-transform duration-[160ms] ${open ? "rotate-90" : ""}`}
          aria-hidden="true"
        />
        <Wrench size={12} className="text-tertiary" aria-hidden="true" />
        <span className="font-mono text-primary">{call.tool}</span>
        <span className="ml-auto inline-flex items-center gap-1">
          {state === "running" && (
            <>
              <Loader2 size={11} className="animate-spin text-status-running" aria-hidden="true" />
              <span className="text-tertiary">running</span>
            </>
          )}
          {state === "ok" && (
            <>
              <CheckCircle2 size={11} className="text-status-running" aria-hidden="true" />
              <span className="text-status-running">ok</span>
            </>
          )}
          {state === "err" && (
            <>
              <AlertCircle size={11} className="text-status-error" aria-hidden="true" />
              <span className="text-status-error">error</span>
            </>
          )}
        </span>
      </button>
      {open && (
        <div className="border-t border-border-subtle px-3 py-2">
          <div className="micro mb-1">args</div>
          <pre className="bg-sunken text-secondary border border-border-subtle rounded-sm p-2 font-mono text-[11px] leading-[16px] whitespace-pre-wrap break-all">
            {fmt(call.args)}
          </pre>
          {call.result !== undefined && (
            <>
              <div className="micro mt-2 mb-1">result</div>
              <pre className="bg-sunken text-secondary border border-border-subtle rounded-sm p-2 font-mono text-[11px] leading-[16px] whitespace-pre-wrap break-all">
                {fmt(call.result)}
              </pre>
            </>
          )}
          {call.error && (
            <>
              <div className="micro mt-2 mb-1 text-status-error">error</div>
              <pre className="bg-status-error/10 border border-status-error/30 text-status-error rounded-sm p-2 font-mono text-[11px] leading-[16px] whitespace-pre-wrap break-all">
                {call.error}
              </pre>
            </>
          )}
        </div>
      )}
    </div>
  );
}
